import { useState, useEffect, useCallback } from 'react';
import { ChevronUp } from 'lucide-react';
import '../styles/ScrollToTop.scss';

// Throttle function to limit the frequency of execution
const throttle = (func: Function, delay: number) => {
  let lastCall = 0;
  return (...args: any[]) => {
    const now = Date.now();
    if (now - lastCall < delay) return;
    lastCall = now;
    return func(...args);
  };
};

const ScrollToTop = () => {
  const [isVisible, setIsVisible] = useState(false);

  // Show button when page is scrolled down
  const toggleVisibility = useCallback(() => {
    if (window.scrollY > 300) {
      setIsVisible(true);
    } else {
      setIsVisible(false);
    }
  }, []);

  // Create throttled version of scroll handler
  const throttledToggleVisibility = useCallback(
    throttle(toggleVisibility, 200),
    [toggleVisibility]
  );

  // Set up scroll event listener
  useEffect(() => {
    window.addEventListener('scroll', throttledToggleVisibility);
    return () => window.removeEventListener('scroll', throttledToggleVisibility);
  }, [throttledToggleVisibility]);

  // Scroll to top function with focus management
  const scrollToTop = () => {
    // Remove any hash from the URL to avoid conflicts
    if (window.location.hash) {
      window.history.pushState("", document.title, window.location.pathname + window.location.search);
    }
    
    // First scroll to top
    window.scrollTo({
      top: 0,
      behavior: 'smooth'
    });
    
    // After scrolling completes, set focus to the first focusable element
    setTimeout(() => {
      // Try to find the main element or a header to focus on
      const mainContent = document.querySelector('main') || document.querySelector('h1');
      
      if (mainContent) {
        // Make it focusable if it's not already
        if (!mainContent.hasAttribute('tabindex')) {
          mainContent.setAttribute('tabindex', '-1');
        }
        
        // Focus the element
        (mainContent as HTMLElement).focus({ preventScroll: true });
        
        // Remove tabindex after blur to not affect normal page navigation
        if (mainContent.getAttribute('tabindex') === '-1') {
          mainContent.addEventListener('blur', () => {
            mainContent.removeAttribute('tabindex');
          }, { once: true });
        }
      }
    }, 500); // Wait for smooth scrolling to complete
  };

  return (
    <div 
      className={`scroll-to-top ${isVisible ? 'scroll-to-top--visible' : 'scroll-to-top--hidden'}`}
      role="navigation"
      aria-label="Scroll to top navigation"
    >
      <button
        onClick={scrollToTop}
        className="scroll-to-top__button"
        aria-label="Scroll to top"
        title="Scroll to top"
        tabIndex={isVisible ? 0 : -1}
      >
        <ChevronUp className="scroll-to-top__icon" aria-hidden="true" />
      </button>
    </div>
  );
};

export default ScrollToTop; 